import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Users, Video, Clock, CheckSquare, Bell, BookOpen, ArrowRight, HelpCircle, History, MessageSquare, Briefcase, Building } from "lucide-react";
import { RelatedAlternatives } from "@/components/RelatedAlternatives";

const Quill = () => {
  const faqData = [
    {
      question: "What happened to Quill?",
      answer: "Quill was a workplace messaging app that was acquired by Slack in 2016 and subsequently discontinued. The team joined Slack to help improve their enterprise communication platform."
    },
    {
      question: "Why did Quill shut down?",
      answer: "Quill was acquired by Slack as part of Slack's strategy to acquire talented teams and technology in the workplace communication space. After the acquisition, the standalone Quill app was discontinued."
    },
    {
      question: "What is the best Quill alternative for study groups?",
      answer: "CramStudyWith.me is an excellent alternative for educational team communication. It offers Study Stream for video collaboration, group study rooms, task management, and productivity tools specifically designed for learning environments."
    },
    {
      question: "Can I use CramStudyWith.me for team communication?",
      answer: "Yes! CramStudyWith.me provides modern collaboration features including video study rooms, shared task management, and group learning tools perfect for academic teams and study groups."
    },
    {
      question: "Is CramStudyWith.me free to use?",
      answer: "Yes, CramStudyWith.me offers free access to core features including Study Stream, Study Timer, Task Management, and group study capabilities. It's a comprehensive platform for students and learners."
    },
    {
      question: "What features does CramStudyWith.me offer for group collaboration?",
      answer: "CramStudyWith.me offers Study Stream for live video sessions, group study rooms for collaborative learning, task management for organizing group projects, reminders for deadlines, and productivity timers for focused work."
    }
  ];

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Quill Alternative - Free Study Rooms | CramStudyWith.me",
    "description": "Looking for a Quill alternative? Quill was acquired by Slack but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for team collaboration.",
    "url": "https://cramstudywith.me/quill/",
    "inLanguage": "en",
    "isPartOf": {
      "@type": "WebSite",
      "name": "CramStudyWith.me",
      "url": "https://cramstudywith.me"
    },
    "about": {
      "@type": "Thing",
      "name": "Quill Alternative"
    },
    "mentions": {
      "@type": "SoftwareApplication",
      "name": "Quill",
      "applicationCategory": "CommunicationApplication"
    },
    "keywords": "Quill alternative, Quill replacement, Quill shutdown, team messaging, workplace communication, study groups"
  };

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "CramStudyWith.me",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web Browser",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "5",
      "ratingCount": "1000"
    },
    "featureList": [
      "Study Stream - Live video study sessions",
      "Study Timer - Pomodoro and custom timers",
      "Task Management - Organize assignments",
      "Group Study Rooms - Collaborative learning",
      "Reminders - Never miss deadlines",
      "Flash Cards - Active recall learning",
      "Quizzes - Test your knowledge",
      "Team Collaboration - Work together"
    ],
    "publisher": {
      "@type": "Organization",
      "name": "CramStudyWith.me"
    }
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Helmet>
        <title>Quill Alternative - Free Study Rooms | CramStudyWith.me</title>
        <meta name="description" content="Looking for a Quill alternative? Quill was acquired by Slack but CramStudyWith.me offers free Study Stream, Study Timer, and Task Management for team collaboration." />
        <meta name="keywords" content="Quill alternative, Quill replacement, Quill shutdown, team messaging, workplace communication, study groups, CramStudyWith.me" />
        <link rel="canonical" href="https://cramstudywith.me/quill/" />
        <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-20 bg-gradient-to-br from-primary/10 via-background to-secondary/10">
          <div className="container mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-destructive/10 text-destructive px-4 py-2 rounded-full mb-6">
              <Briefcase className="h-4 w-4" />
              <span className="text-sm font-medium">Quill Acquired by Slack in 2016</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Quill Alternative
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Quill is no longer available as a standalone app. Discover CramStudyWith.me - the modern platform for <Link to="/study-with-me/" className="text-primary hover:underline">collaborative studying</Link> with video rooms, team collaboration, and productivity tools.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/studystream/">
                <Button size="lg" className="gap-2">
                  <Video className="h-5 w-5" />
                  Try Study Stream Free
                </Button>
              </Link>
              <Link to="/quill/history/">
                <Button size="lg" variant="outline" className="gap-2">
                  <History className="h-5 w-5" />
                  Quill History
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* What Happened Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold mb-6 text-center">What Happened to Quill?</h2>
              <div className="bg-card rounded-lg p-8 shadow-lg">
                <p className="text-lg text-muted-foreground mb-4">
                  Quill was a workplace messaging application designed for professional team communication. In <strong>2016, Quill was acquired by Slack</strong>, one of the leading workplace communication platforms.
                </p>
                <p className="text-lg text-muted-foreground mb-4">
                  Following the acquisition, the Quill team joined Slack to contribute to their product development, and the standalone Quill app was discontinued. This is a common pattern in the tech industry where larger companies acquire smaller competitors for their talent and technology.
                </p>
                <p className="text-lg text-muted-foreground">
                  If you're looking for team collaboration tools for studying, <Link to="/online-workspace/" className="text-primary hover:underline">CramStudyWith.me</Link> offers modern features including <Link to="/productivity-tool/" className="text-primary hover:underline">productivity tools</Link>, video collaboration, and <Link to="/study-with-friends/" className="text-primary hover:underline">group study capabilities</Link>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Features Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-12 text-center">Quill vs CramStudyWith.me</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Video className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Stream</CardTitle>
                  <CardDescription>Live video study sessions</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Go beyond text messaging with <Link to="/studystream/" className="text-primary hover:underline">Study Stream</Link> - real-time video collaboration for immersive team study experiences.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Users className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Group Study Rooms</CardTitle>
                  <CardDescription>Virtual study spaces</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Create focused <Link to="/group-study/" className="text-primary hover:underline">study groups</Link> with video, audio, and collaborative tools - purpose-built for learning together.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Clock className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Study Timer</CardTitle>
                  <CardDescription>Pomodoro technique</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Stay focused with our <Link to="/study-timer/" className="text-primary hover:underline">Study Timer</Link> featuring customizable Pomodoro sessions for productivity.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <CheckSquare className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Task Management</CardTitle>
                  <CardDescription>Organize team projects</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Manage team assignments and projects with our <Link to="/task-management/" className="text-primary hover:underline">Task Management</Link> system for organized collaboration.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <Bell className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Smart Reminders</CardTitle>
                  <CardDescription>Never miss deadlines</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Get intelligent <Link to="/reminders/" className="text-primary hover:underline">reminders</Link> for study sessions and deadlines to keep your team on track.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-primary/50 transition-colors">
                <CardHeader>
                  <BookOpen className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>Learning Tools</CardTitle>
                  <CardDescription>Flash cards & quizzes</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">
                    Access <Link to="/flash-cards/" className="text-primary hover:underline">flash cards</Link> and <Link to="/quizzes/" className="text-primary hover:underline">quizzes</Link> for active learning - educational features for team learning.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Blog Links Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Learn More About Team Studying</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link to="/blog/top-10-digital-tools-for-academic-success/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">Top 10 Digital Tools for Academic Success</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/apps-boost-productivity-college/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">Apps That Boost Productivity in College</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
              <Link to="/blog/science-behind-study-with-me/" className="group">
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg group-hover:text-primary transition-colors">The Science Behind Study With Me</CardTitle>
                  </CardHeader>
                </Card>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-3 mb-8">
                <HelpCircle className="h-8 w-8 text-primary" />
                <h2 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Frequently Asked Questions
                </h2>
              </div>
              <Accordion type="single" collapsible className="w-full">
                {faqData.map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`}>
                    <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                    <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-primary to-secondary">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Ready for Better Team Collaboration?
            </h2>
            <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
              Join thousands of students using CramStudyWith.me for focused team study sessions and collaborative learning.
            </p>
            <Link to="/studystream/">
              <Button size="lg" variant="secondary" className="gap-2">
                Get Started Free
                <ArrowRight className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <RelatedAlternatives currentPlatform="Quill" />

      <Footer />
    </div>
  );
};

export default Quill;